import "../../../css/SettingsTab.css";
import React, { useState, useEffect } from "react";
import { Input, Switch, Select, Button, message } from "antd";
import {
  updateSubscription,
  getSyncSchedule,
  rotateWebhookSecret,
  getOAuthAuthorizeUrl,
} from "../../../services/integrations/klaviyoSubscriptionService";
import DisconnectModal from "./DisconnectModal";
import WebhookSecretModal from "./WebhookSecretModal";

const SCHEDULE_OPTIONS = [
  { value: "daily_03_utc", label: "Daily at 03:00 UTC" },
  { value: "hourly", label: "Hourly" },
  { value: "every_6_hours", label: "Every 6 hours" },
  { value: "manual_only", label: "Manual only" },
];

export default function SettingsTab({ subscription, onRefresh, onDisconnect }) {
  const [name, setName] = useState(subscription?.name || "");
  const [enabled, setEnabled] = useState(subscription?.enabled ?? true);
  const [schedule, setSchedule] = useState(subscription?.syncSchedule || "daily_03_utc");
  const [nextSync, setNextSync] = useState(null);
  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
  const [webhookSecretModalOpen, setWebhookSecretModalOpen] = useState(false);
  const [webhookSecret, setWebhookSecret] = useState(null);
  const [rotatingSecret, setRotatingSecret] = useState(false);

  useEffect(() => {
    setName(subscription?.name || "");
    setEnabled(subscription?.enabled ?? true);
    setSchedule(subscription?.syncSchedule || "daily_03_utc");
  }, [subscription]);

  useEffect(() => {
    fetchNextSync();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchNextSync = async () => {
    try {
      const res = await getSyncSchedule();
      setNextSync(res?.nextFireTime || null);
    } catch {
      setNextSync(null);
    }
  };

  const formatNextSync = () => {
    if (schedule === "manual_only") return "Manual triggers only";
    if (!nextSync) return "";
    const d = new Date(nextSync);
    return "Next sync: " + d.toLocaleString("en-US", {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZoneName: "short",
    });
  };

  const handleWebhookSecret = async () => {
    setRotatingSecret(true);
    try {
      const res = await rotateWebhookSecret();
      setWebhookSecret(res?.secret || res?.webhookSecret);
      setWebhookSecretModalOpen(true);
    } catch {
      message.error("Failed to generate webhook secret");
    }
    setRotatingSecret(false);
  };

  const handleWebhookSecretDismiss = () => {
    setWebhookSecretModalOpen(false);
    setWebhookSecret(null);
    onRefresh();
  };

  const formatWebhookDate = () => {
    if (!subscription?.webhookSecretRotatedAt) return null;
    const d = new Date(subscription.webhookSecretRotatedAt);
    return "Last rotated: " + d.toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
    });
  };

  const handleNameBlur = async () => {
    if (name.trim() === (subscription?.name || "")) return;
    try {
      await updateSubscription({ name: name.trim() });
      message.success("Subscription name updated");
      onRefresh();
    } catch {
      message.error("Failed to update subscription name");
      setName(subscription?.name || "");
    }
  };

  const handleEnabledChange = async (val) => {
    const prev = enabled;
    setEnabled(val);
    try {
      await updateSubscription({ enabled: val });
      message.success(val ? "Integration enabled" : "Integration disabled");
      onRefresh();
    } catch {
      setEnabled(prev);
      message.error("Failed to update status");
    }
  };

  const handleScheduleChange = async (val) => {
    const prev = schedule;
    setSchedule(val);
    try {
      await updateSubscription({ syncSchedule: val });
      message.success("Sync schedule updated");
      await fetchNextSync();
      onRefresh();
    } catch {
      setSchedule(prev);
      message.error("Failed to update sync schedule");
    }
  };

  return (
    <div className="settings-tab-container">
      {/* Connection */}
      <div className="settings-section">
        <div className="settings-section-title">Connection</div>

        <div className="settings-row">
          <div className="settings-field">
            <div className="settings-label">Subscription Name</div>
          </div>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={handleNameBlur}
            onPressEnter={handleNameBlur}
            style={{ width: 300 }}
          />
        </div>

        {subscription?.authMode === "OAUTH_REAUTH_REQUIRED" ? (
          <div className="settings-row">
            <div className="settings-field">
              <div className="settings-label">Authentication</div>
              <div className="settings-hint" style={{ color: "#d4380d" }}>
                OAuth connection expired or was revoked. Please reconnect.
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 13, color: "#d4380d", fontWeight: 500 }}>
                Reconnection required
              </span>
              <Button type="primary" size="small" danger onClick={() => {
                getOAuthAuthorizeUrl().then(url => { window.location.href = url; });
              }}>
                Reconnect to Klaviyo
              </Button>
            </div>
          </div>
        ) : (
          <div className="settings-row">
            <div className="settings-field">
              <div className="settings-label">Authentication</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 13, color: "#059669", fontWeight: 500 }}>
                Connected via OAuth
              </span>
              <Button size="small" onClick={() => {
                getOAuthAuthorizeUrl().then(url => { window.location.href = url; });
              }}>
                Reconnect
              </Button>
            </div>
          </div>
        )}

        <div className="settings-row">
          <div className="settings-field">
            <div className="settings-label">Status</div>
          </div>
          <Switch
            checked={enabled}
            onChange={handleEnabledChange}
            checkedChildren="Enabled"
            unCheckedChildren="Disabled"
          />
        </div>
      </div>

      {/* Catalog Sync */}
      <div className="settings-section">
        <div className="settings-section-title">Catalog Sync</div>

        <div className="settings-row">
          <div className="settings-field">
            <div className="settings-label">Sync Schedule</div>
            {schedule !== "manual_only" && nextSync && (
              <div className="settings-hint">{formatNextSync()}</div>
            )}
            {schedule === "manual_only" && (
              <div className="settings-hint">Manual triggers only</div>
            )}
          </div>
          <Select
            value={schedule}
            onChange={handleScheduleChange}
            style={{ width: 220 }}
            options={SCHEDULE_OPTIONS}
          />
        </div>
      </div>

      {/* Danger Zone */}
      <div className="danger-zone">
        <div className="settings-section-title">Danger Zone</div>
        <div className="danger-description">
          Disconnect this Klaviyo integration. This will disable all event mappings and
          stop sending data to Klaviyo.
        </div>
        <Button className="danger-button" onClick={() => setDisconnectModalOpen(true)}>
          Disconnect
        </Button>
      </div>

      <DisconnectModal
        visible={disconnectModalOpen}
        onClose={() => setDisconnectModalOpen(false)}
        onDisconnect={onDisconnect}
      />

      <WebhookSecretModal
        visible={webhookSecretModalOpen}
        secret={webhookSecret}
        onDismiss={handleWebhookSecretDismiss}
      />
    </div>
  );
}
